import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Alert, Col, Row, Card, Form, Button, InputGroup, Spinner } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faMapMarkerAlt, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL, END_POINT_COMPANY } from "../const";
import { useNavigate, Link } from 'react-router-dom';
import { showSuccessToast } from "../utils/toast";

export const CompanyForm = ({ onSuccess, onCancel, onSubmittingChange }) => {
  const token = sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');

  const formik = useFormik({
    initialValues: { name: "", address: "" },
    validationSchema: Yup.object({
      name: Yup.string().required("Company Name is required"),
      address: Yup.string().required("Company Address is required"),
    }),
    onSubmit: async (values, { setSubmitting, setStatus, setErrors }) => {
      setStatus(null);
      onSubmittingChange?.(true);
      let result;
      try {
        const response = await axios.post(API_URL + END_POINT_COMPANY, values, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.data.status) {
          setStatus(response.data.message || 'Failed to create company.');
          return;
        }
        result = response.data;
      } catch (error) {
        const errors = error.response?.data?.errors;
        if (errors) {
          setErrors(Object.fromEntries(
            Object.entries(errors).map(([field, messages]) => [
              field, Array.isArray(messages) ? messages[0] : messages,
            ])
          ));
        }
        setStatus(error.response?.data?.message || 'Failed to create company.');
      } finally {
        setSubmitting(false);
        onSubmittingChange?.(false);
      }
      if (result) onSuccess(result);
    },
  });

  return (
    <Form noValidate onSubmit={formik.handleSubmit} aria-busy={formik.isSubmitting}>
      {formik.status && (
        <Alert variant="danger" className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>{formik.status}</span>
        </Alert>
      )}
      <Row>
        <Col md={6} className="mb-3">
          <Form.Group controlId="company-name">
            <Form.Label>Company Name <span className="text-danger">*</span></Form.Label>
            <InputGroup className="has-validation">
              <InputGroup.Text><FontAwesomeIcon icon={faBuilding} /></InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="e.g. Acme Corporation"
                autoFocus
                disabled={formik.isSubmitting}
                {...formik.getFieldProps("name")}
                isInvalid={formik.touched.name && formik.errors.name}
              />
              <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
            </InputGroup>
            <Form.Text className="text-muted">This is the legal or trading name shown across the app.</Form.Text>
          </Form.Group>
        </Col>
        <Col md={6} className="mb-3">
          <Form.Group controlId="company-address">
            <Form.Label>
              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-muted me-1" />
              Company Address <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Street, city, state, postal code"
              disabled={formik.isSubmitting}
              {...formik.getFieldProps("address")}
              isInvalid={formik.touched.address && formik.errors.address}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.address}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <hr className="my-3" />
      <div className="create-record-form__actions d-flex justify-content-end gap-2 mt-2">
        {onCancel && (
          <Button variant="outline-secondary" type="button" onClick={onCancel} disabled={formik.isSubmitting}>
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          className="btn-quote-blue py-2 px-3 box-shedow-none outline-none d-inline-flex align-items-center gap-2"
          type="submit"
          disabled={formik.isSubmitting}
        >
          {formik.isSubmitting && <Spinner as="span" animation="border" size="sm" role="status" />}
          {formik.isSubmitting ? 'Saving...' : 'Save Company'}
        </Button>
      </div>
    </Form>
  );
};

export const CreateCompanyForm = () => {
  const navigate = useNavigate();

  const handleSuccess = async (result) => {
    showSuccessToast(result.message)
    navigate('/company');
  };

  return (
    <Card border="light" className="bg-white shadow-sm mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0">Add Company</h5>
          <Link to="/company" className="text-decoration-none btn-quote-back d-inline-flex align-items-center">
            Back to Listing
          </Link>
        </div>
        <CompanyForm onSuccess={handleSuccess} />
      </Card.Body>
    </Card>
  );
};
